// ---------------------------------------------------------------------------
// DataLoader -- Progressive Loading for DataTable
// ---------------------------------------------------------------------------
// Shows loading progress while fetching data from API.
// Adapts messages based on the data source type and variant (initial/loadMore).
// ---------------------------------------------------------------------------

import React, { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StepStatus = 'pending' | 'loading' | 'done'

interface LoadingStep {
  readonly id: string
  readonly label: string
  status: StepStatus
}

type DataSourceId = 'profiles' | 'content' | 'comments' | 'brands' | 'teams' | 'hashtags' | 'channels' | 'trends' | 'compare' | 'overlap' | 'funnel'

type LoaderVariant = 'initial' | 'loadMore'

interface DataLoaderProps {
  dataSource: DataSourceId
  variant?: LoaderVariant
}

// ---------------------------------------------------------------------------
// Config per data source
// ---------------------------------------------------------------------------

const LOADER_CONFIG: Record<DataSourceId, {
  titles: Record<LoaderVariant, string>
  steps: { initial: string; loadMore: string }[]
}> = {
  profiles: {
    titles: {
      initial: 'Loading profiles',
      loadMore: 'Loading more profiles',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Filtering audience profiles', loadMore: 'Filtering audience profiles' },
      { initial: 'Loading profile data', loadMore: 'Loading profile data' },
      { initial: 'Preparing table', loadMore: 'Adding to table' },
    ],
  },
  content: {
    titles: {
      initial: 'Loading content',
      loadMore: 'Loading more content',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Fetching content posts', loadMore: 'Fetching content posts' },
      { initial: 'Processing media data', loadMore: 'Processing media data' },
      { initial: 'Preparing table', loadMore: 'Adding to table' },
    ],
  },
  comments: {
    titles: {
      initial: 'Loading comments',
      loadMore: 'Loading more comments',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Fetching comment data', loadMore: 'Fetching comment data' },
      { initial: 'Analyzing sentiment', loadMore: 'Analyzing sentiment' },
      { initial: 'Preparing table', loadMore: 'Adding to table' },
    ],
  },
  brands: {
    titles: {
      initial: 'Loading brands',
      loadMore: 'Loading more brands',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Fetching brand mentions', loadMore: 'Fetching brand mentions' },
      { initial: 'Processing detections', loadMore: 'Processing detections' },
      { initial: 'Preparing table', loadMore: 'Adding to table' },
    ],
  },
  teams: {
    titles: {
      initial: 'Loading teams',
      loadMore: 'Loading more teams',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Fetching team mentions', loadMore: 'Fetching team mentions' },
      { initial: 'Processing data', loadMore: 'Processing data' },
      { initial: 'Preparing table', loadMore: 'Adding to table' },
    ],
  },
  hashtags: {
    titles: {
      initial: 'Loading hashtags',
      loadMore: 'Loading more hashtags',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Fetching hashtag data', loadMore: 'Fetching hashtag data' },
      { initial: 'Processing mentions', loadMore: 'Processing mentions' },
      { initial: 'Preparing table', loadMore: 'Adding to table' },
    ],
  },
  channels: {
    titles: {
      initial: 'Loading channels',
      loadMore: 'Loading more channels',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Fetching channel data', loadMore: 'Fetching channel data' },
      { initial: 'Loading performance metrics', loadMore: 'Loading performance metrics' },
      { initial: 'Preparing table', loadMore: 'Adding to table' },
    ],
  },
  trends: {
    titles: {
      initial: 'Loading trends',
      loadMore: 'Loading trends',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Fetching temporal data', loadMore: 'Fetching temporal data' },
      { initial: 'Processing time series', loadMore: 'Processing time series' },
      { initial: 'Preparing chart', loadMore: 'Preparing chart' },
    ],
  },
  compare: {
    titles: {
      initial: 'Comparing subjects',
      loadMore: 'Comparing subjects',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Analyzing audience data', loadMore: 'Analyzing audience data' },
      { initial: 'Calculating metrics', loadMore: 'Calculating metrics' },
      { initial: 'Preparing results', loadMore: 'Preparing results' },
    ],
  },
  overlap: {
    titles: {
      initial: 'Calculating overlap',
      loadMore: 'Calculating overlap',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Analyzing shared profiles', loadMore: 'Analyzing shared profiles' },
      { initial: 'Processing demographics', loadMore: 'Processing demographics' },
      { initial: 'Preparing results', loadMore: 'Preparing results' },
    ],
  },
  funnel: {
    titles: {
      initial: 'Loading audience data',
      loadMore: 'Loading audience data',
    },
    steps: [
      { initial: 'Connecting to database', loadMore: 'Connecting to database' },
      { initial: 'Analyzing persona profiles', loadMore: 'Analyzing persona profiles' },
      { initial: 'Processing demographics', loadMore: 'Processing demographics' },
      { initial: 'Preparing insights', loadMore: 'Preparing insights' },
    ],
  },
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Helper to generate random delay within range
function randomDelay(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export function DataLoader({ dataSource, variant = 'initial' }: DataLoaderProps) {
  const config = LOADER_CONFIG[dataSource]
  const title = config.titles[variant]
  const stepLabels = config.steps.map(s => s[variant])

  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [steps, setSteps] = useState<LoadingStep[]>(
    stepLabels.map((label, i) => ({
      id: `step-${i}`,
      label,
      status: i === 0 ? 'loading' : 'pending',
    }))
  )
  const [extraProgress, setExtraProgress] = useState(0)

  // Simulate progressive loading steps with random delays
  useEffect(() => {
    const timers: NodeJS.Timeout[] = []

    // Random delays for each step (step 1 is fastest - just connecting)
    const step1Delay = randomDelay(150, 300)
    const step2Delay = randomDelay(500, 1000)
    const step3Delay = randomDelay(400, 800)

    // Step 1: Connect
    timers.push(setTimeout(() => {
      setSteps(prev => prev.map((s, i) =>
        i === 0 ? { ...s, status: 'done' } : s
      ))
      setCurrentStepIndex(1)

      // Step 2: Filter/Fetch
      timers.push(setTimeout(() => {
        setSteps(prev => prev.map((s, i) =>
          i === 1 ? { ...s, status: 'loading' } : s
        ))
      }, 50))

      timers.push(setTimeout(() => {
        setSteps(prev => prev.map((s, i) =>
          i === 1 ? { ...s, status: 'done' } : s
        ))
        setCurrentStepIndex(2)

        // Step 3: Process
        timers.push(setTimeout(() => {
          setSteps(prev => prev.map((s, i) =>
            i === 2 ? { ...s, status: 'loading' } : s
          ))
        }, 50))

        // Continue progressing while on step 3 (simulates ongoing work)
        timers.push(setTimeout(() => {
          setExtraProgress(randomDelay(5, 15))
        }, step3Delay))

        timers.push(setTimeout(() => {
          setExtraProgress(randomDelay(15, 25))
        }, step3Delay + randomDelay(400, 800)))

      }, step2Delay))
    }, step1Delay))

    return () => timers.forEach(clearTimeout)
  }, [variant])

  // Calculate progress: base progress from steps + extra progress while waiting
  const baseProgress = ((steps.filter(s => s.status === 'done').length +
    (currentStepIndex < steps.length ? 0.5 : 0)) / steps.length) * 100
  const progress = Math.min(baseProgress + extraProgress, 95) // Cap at 95% until actually done

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="rounded-xl border border-v11-border bg-v11-surface p-6 w-[500px] mx-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm font-semibold text-v11-text-primary">
            {title}
          </span>
        </div>
        <span className="text-xs font-bold text-primary tabular-nums">
          {Math.round(progress)}%
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-1.5 rounded-full bg-v11-bg-secondary overflow-hidden mb-4">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-primary to-primary/80"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      </div>

      {/* Single Step with Animation */}
      <AnimatePresence mode="wait">
        <motion.div
          key={steps[currentStepIndex].id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15, ease: 'easeInOut' }}
          className="flex items-center gap-2 px-2 py-1.5 rounded"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary flex-shrink-0" />
          <span className="text-xs text-v11-text-primary font-medium">
            {steps[currentStepIndex].label}
          </span>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  )
}
